import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { parseNumber } from "./numberInput";

export type Units = "metric" | "imperial";
export type MeasureKind = "height" | "weight" | "length";

export const RANGES: Record<MeasureKind, { min: number; max: number }> = { height: { min: 100, max: 250 }, weight: { min: 30, max: 300 }, length: { min: 10, max: 250 } };
const CM_PER_IN = 2.54, KG_PER_LB = 0.45359237;

const round = (n: number, d = 1) => String(Math.round(n * 10 ** d) / 10 ** d);

export function UnitToggle({ units, onChange }: { units: Units; onChange: (u: Units) => void }) {
  return (
    <div className="flex gap-2 mb-4 justify-center">
      <Button size="sm" variant={units === "metric" ? "default" : "outline"} onClick={() => onChange("metric")}>Metric (kg / cm)</Button>
      <Button size="sm" variant={units === "imperial" ? "default" : "outline"} onClick={() => onChange("imperial")}>Imperial (lb / ft-in)</Button>
    </div>
  );
}

/**
 * A height, weight or length box in the chosen units. Reports the value in cm / kg (or null when empty or out of range) and shows
 * a plain message for values that are out of range (for example "1.70" typed into a centimetre box).
 */
export function MeasureField({ label, kind, units, initialMetric, onValue, ariaLabel, optional }: {
  label: string; kind: MeasureKind; units: Units; initialMetric: number; onValue: (metric: number | null) => void; ariaLabel?: string; optional?: boolean;
}) {
  const [metric, setMetric] = useState<number | null>(optional ? null : initialMetric);
  const [text, setText] = useState(() => (optional ? "" : round(initialMetric)));
  const [ft, setFt] = useState(() => String(Math.floor(initialMetric / CM_PER_IN / 12)));
  const [inch, setInch] = useState(() => round((initialMetric / CM_PER_IN) % 12));
  const [message, setMessage] = useState("");
  const prevUnits = useRef(units);

  const report = (v: number | null, raw: string) => {
    const r = RANGES[kind];
    const empty = raw.trim() === "";
    let msg = "";
    if (v != null && (v < r.min || v > r.max)) {
      if (kind === "height" && units === "metric" && v >= 0.5 && v <= 2.5) msg = `That looks like metres. Enter your height in centimetres (for example ${Math.round(v * 100)}).`;
      else msg = `Enter a ${kind} between ${Math.round(kind === "weight" && units === "imperial" ? r.min / KG_PER_LB : kind !== "weight" && units === "imperial" ? r.min / CM_PER_IN : r.min)} and ${Math.round(kind === "weight" && units === "imperial" ? r.max / KG_PER_LB : kind !== "weight" && units === "imperial" ? r.max / CM_PER_IN : r.max)} ${kind === "weight" ? (units === "metric" ? "kg" : "lb") : units === "metric" ? "cm" : "in"}.`;
      v = null;
    } else if (v == null && empty && optional) msg = "";
    else if (v == null) msg = empty ? `Enter your ${label.toLowerCase()}.` : "Numbers only, please.";
    setMessage(msg); setMetric(v); onValue(v);
  };

  // Converting when the units change keeps the person's value instead of resetting it.
  useEffect(() => {
    if (prevUnits.current === units) { report(metric, units === "metric" || kind !== "height" ? text : "x"); return; }
    prevUnits.current = units;
    if (optional && metric == null) { setText(""); setMessage(""); return; }
    const m = metric ?? initialMetric;
    if (units === "metric") setText(round(kind === "weight" ? m : m));
    else if (kind === "weight") setText(round(m / KG_PER_LB));
    else if (kind === "height") { const tot = m / CM_PER_IN; setFt(String(Math.floor(tot / 12))); setInch(round(tot % 12)); }
    else setText(round(m / CM_PER_IN));
    setMetric(m); onValue(m); setMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units]);

  const unitLabel = kind === "weight" ? (units === "metric" ? "kg" : "lb") : units === "metric" ? "cm" : "in";
  if (kind === "height" && units === "imperial") {
    const upd = (f: string, i: string) => {
      const n1 = parseNumber(f), n2 = parseNumber(i) ?? 0;
      if (n2 < 0 || n2 >= 12) { setMessage("Inches must be between 0 and 11.9. Put whole feet in the feet box."); setMetric(null); onValue(null); return; }
      report(n1 == null ? null : (n1 * 12 + n2) * CM_PER_IN, f);
    };
    return (
      <div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{label} (ft)</Label><Input inputMode="decimal" aria-label={`${ariaLabel ?? label} ft`} value={ft} onChange={(e) => { setFt(e.target.value); upd(e.target.value, inch); }} /></div>
          <div><Label>{label} (in)</Label><Input inputMode="decimal" aria-label={`${ariaLabel ?? label} in`} value={inch} onChange={(e) => { setInch(e.target.value); upd(ft, e.target.value); }} /></div>
        </div>
        {message && <p className="text-xs text-red-600 mt-1" data-testid="measure-error">{message}</p>}
      </div>
    );
  }
  return (
    <div>
      <Label>{label} ({unitLabel})</Label>
      <Input inputMode="decimal" aria-label={ariaLabel ?? label} value={text} onChange={(e) => {
        setText(e.target.value);
        const n = parseNumber(e.target.value);
        report(n == null ? null : units === "metric" ? n : kind === "weight" ? n * KG_PER_LB : n * CM_PER_IN, e.target.value);
      }} />
      {message && <p className="text-xs text-red-600 mt-1" data-testid="measure-error">{message}</p>}
    </div>
  );
}
